import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function Login() {
  const { login, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/dashboard");
    } catch (e2) {
      const msg = formatApiErrorDetail(e2.response?.data?.detail) || "Please try again.";
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-brand-navy text-white">
      <div className="relative hidden lg:block">
        <div className="absolute inset-0 grain-overlay">
          <div className="absolute -top-24 -left-24 h-[500px] w-[500px] rounded-full bg-brand-magenta/25 blur-3xl" />
          <div className="absolute -bottom-24 -right-24 h-[500px] w-[500px] rounded-full bg-brand-teal/25 blur-3xl" />
        </div>
        <div className="relative h-full flex flex-col justify-between p-12">
          <Link to="/" data-testid="login-back-home"><Logo variant="light" /></Link>
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-brand-teal">Welcome back</p>
            <h1 className="mt-4 font-display font-black text-4xl xl:text-5xl leading-tight tracking-tight">
              Capacity is built every day.
            </h1>
            <p className="mt-6 max-w-md text-white/75 text-lg leading-relaxed">
              Pick up where you left off — log a win, check a goal, share a note with your team.
            </p>
          </div>
        </div>
      </div>
      <div className="flex flex-col justify-center px-6 sm:px-12 py-10 bg-brand-sand text-brand-navy">
        <div className="w-full max-w-md mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-brand-mute hover:text-brand-navy text-sm mb-8" data-testid="login-back-link">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <h2 className="font-display font-bold text-3xl tracking-tight">Sign in</h2>
          <p className="mt-2 text-brand-mute">To your Christine Kaatz participant space.</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="login-email">Email</Label>
              <Input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="mt-2 h-11"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="login-password">Password</Label>
              <Input
                id="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="mt-2 h-11"
                data-testid="login-password-input"
              />
            </div>
            {err && <p className="text-sm text-red-600" data-testid="login-error">{err}</p>}
            <Button
              type="submit"
              disabled={busy}
              className="w-full rounded-full h-12 bg-brand-teal hover:bg-brand-tealDark text-white btn-lift"
              data-testid="login-submit-button"
            >
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <p className="mt-6 text-sm text-brand-mute">
            Don't have an account?{" "}
            <Link to="/register" className="text-brand-magenta font-semibold hover:underline" data-testid="login-to-register">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
